import { useCallback, useEffect, useRef, useState } from "react";

export function useScrollReveal(count: number, threshold = 0.35) {
  const [revealed, setRevealed] = useState<Set<number>>(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return new Set(Array.from({ length: count }, (_, i) => i));
    }
    return new Set();
  });
  const [latest, setLatest] = useState<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingRef = useRef<Map<number, HTMLElement>>(new Map());

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const idx = Number((entry.target as HTMLElement).dataset.revealIndex);
          if (Number.isNaN(idx)) return;

          window.requestAnimationFrame(() => {
            setRevealed((prev) => {
              if (prev.has(idx)) return prev;
              const next = new Set(prev);
              next.add(idx);
              return next;
            });
            setLatest(idx);
          });

          observer.unobserve(entry.target);
        });
      },
      {
        threshold,
        rootMargin: "0px 0px -14% 0px",
      },
    );

    observerRef.current = observer;
    pendingRef.current.forEach((el) => observer.observe(el));
    pendingRef.current.clear();

    return () => observer.disconnect();
  }, [threshold]);

  const setRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      if (!el) return;

      el.dataset.revealIndex = String(index);

      if (observerRef.current instanceof IntersectionObserver) {
        observerRef.current.observe(el);
      } else {
        pendingRef.current.set(index, el);
      }
    },
    [],
  );

  return { revealed, latest, setRef };
}
